import {
  countryCodeFromProvinceOrState,
  ProvinceAndStateMappings,
  provinceOrStateCodeFromString,
} from 'utils/province_or_state';
import type { SearchParameters } from '../types';

export interface ResolvedLocation {
  city: string;
  province: string;
  country: 'US' | 'CA';
}

/** US defaults when the same city name exists in US and Canada (US wins unless query says otherwise). */
const AMBIGUOUS_CITY_US_DEFAULT: Record<string, ResolvedLocation> = {
  london: { city: 'London', province: 'KY', country: 'US' },
  cambridge: { city: 'Cambridge', province: 'MA', country: 'US' },
  paris: { city: 'Paris', province: 'TX', country: 'US' },
  springfield: { city: 'Springfield', province: 'MA', country: 'US' },
};

/** Canonical city → province/country (lowercase city key). */
const KNOWN_CITIES: Record<string, { province: string; country: 'US' | 'CA' }> = {
  toronto: { province: 'ON', country: 'CA' },
  vancouver: { province: 'BC', country: 'CA' },
  montreal: { province: 'QC', country: 'CA' },
  calgary: { province: 'AB', country: 'CA' },
  edmonton: { province: 'AB', country: 'CA' },
  ottawa: { province: 'ON', country: 'CA' },
  winnipeg: { province: 'MB', country: 'CA' },
  'quebec city': { province: 'QC', country: 'CA' },
  hamilton: { province: 'ON', country: 'CA' },
  kitchener: { province: 'ON', country: 'CA' },
  windsor: { province: 'ON', country: 'CA' },
  halifax: { province: 'NS', country: 'CA' },
  victoria: { province: 'BC', country: 'CA' },
  saskatoon: { province: 'SK', country: 'CA' },
  regina: { province: 'SK', country: 'CA' },
  'new york': { province: 'NY', country: 'US' },
  'los angeles': { province: 'CA', country: 'US' },
  chicago: { province: 'IL', country: 'US' },
  houston: { province: 'TX', country: 'US' },
  phoenix: { province: 'AZ', country: 'US' },
  philadelphia: { province: 'PA', country: 'US' },
  'san antonio': { province: 'TX', country: 'US' },
  'san diego': { province: 'CA', country: 'US' },
  'san francisco': { province: 'CA', country: 'US' },
  dallas: { province: 'TX', country: 'US' },
  austin: { province: 'TX', country: 'US' },
  seattle: { province: 'WA', country: 'US' },
  denver: { province: 'CO', country: 'US' },
  boston: { province: 'MA', country: 'US' },
  miami: { province: 'FL', country: 'US' },
  atlanta: { province: 'GA', country: 'US' },
  'las vegas': { province: 'NV', country: 'US' },
  portland: { province: 'OR', country: 'US' },
  nashville: { province: 'TN', country: 'US' },
  'north miami': { province: 'FL', country: 'US' },
  'south bend': { province: 'IN', country: 'US' },
  'west palm beach': { province: 'FL', country: 'US' },
  'south bay': { province: 'CA', country: 'US' },
};

const NON_NORTH_AMERICAN_LOCATION_PATTERNS: RegExp[] = [
  /\blondon\s*,?\s*uk\b/i,
  /\blondon\s+uk\b/i,
  /\bparis\s*,?\s*france\b/i,
  /\bparis\s+france\b/i,
  /\bsydney\s*,?\s*australia\b/i,
  /\bberlin\s*,?\s*germany\b/i,
  /\btokyo\s*,?\s*japan\b/i,
];

export const NON_SUPPORTED_LOCATION_CLARIFICATION =
  'I can only search for properties in the United States and Canada. Could you try a city in one of these countries instead?';

export const NON_SUPPORTED_LOCATION_SUGGESTIONS = [
  'New York, NY',
  'Los Angeles, CA',
  'Chicago, IL',
  'Miami, FL',
  'Seattle, WA',
];

export function capitalizeCityName(input: string): string {
  return input
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function detectExplicitNonNorthAmericanLocation(query: string): boolean {
  return NON_NORTH_AMERICAN_LOCATION_PATTERNS.some(pattern => pattern.test(query));
}

export function extractExplicitProvinceOrStateCodeFromQuery(fullQuery: string): string | null {
  const lowerQuery = fullQuery.toLowerCase();

  for (const [code, name] of Object.entries(ProvinceAndStateMappings)) {
    if (lowerQuery.includes(name.toLowerCase())) {
      return code.toUpperCase();
    }
  }

  // Only match trailing / comma-separated codes — avoid "in" (Indiana) inside phrases like "houses in Austin"
  const suffixPatterns = [/,?\s*([A-Za-z]{2})\s*$/, /\s([A-Za-z]{2})\s*$/];
  for (const pattern of suffixPatterns) {
    const match = fullQuery.match(pattern);
    if (match?.[1]) {
      const fromString = provinceOrStateCodeFromString(match[1]);
      if (fromString) {
        return fromString.toUpperCase();
      }
    }
  }

  return null;
}

/**
 * Resolves city + province + country from a city name and full user query.
 * Returns null when location is explicitly outside US/CA or cannot be resolved.
 */
export function inferLocationFromCityAndQuery(
  cityName: string,
  fullQuery: string
): ResolvedLocation | null {
  if (detectExplicitNonNorthAmericanLocation(fullQuery)) {
    return null;
  }

  const city = capitalizeCityName(cityName.trim());
  if (!city) {
    return null;
  }

  const lowerQuery = fullQuery.toLowerCase();
  const lowerCity = city.toLowerCase();

  const known = KNOWN_CITIES[lowerCity];
  if (known) {
    return {
      city,
      province: known.province,
      country: known.country,
    };
  }

  const ambiguousUs = AMBIGUOUS_CITY_US_DEFAULT[lowerCity];
  if (ambiguousUs) {
    return { ...ambiguousUs, city };
  }

  const explicitCode = extractExplicitProvinceOrStateCodeFromQuery(fullQuery);
  if (explicitCode) {
    const country = countryCodeFromProvinceOrState(explicitCode);
    if (country === 'US' || country === 'CA') {
      return {
        city,
        province: explicitCode,
        country,
      };
    }
  }

  if (lowerQuery.includes('canada') || lowerQuery.includes('canadian')) {
    return { city, province: 'ON', country: 'CA' };
  }

  if (
    lowerQuery.includes('usa') ||
    lowerQuery.includes('united states') ||
    lowerQuery.includes('america') ||
    lowerQuery.includes('american')
  ) {
    return { city, province: 'CA', country: 'US' };
  }

  return null;
}

/**
 * Applies authoritative location codes after LLM extraction or rule-based merges.
 */
export function resolveLocationInSearchParams(
  params: SearchParameters,
  userQuery: string
): SearchParameters {
  if (!params.city || typeof params.city !== 'string') {
    return params;
  }

  const city = capitalizeCityName(params.city);
  const resolved = inferLocationFromCityAndQuery(city, userQuery);
  if (!resolved) {
    return { ...params, city };
  }

  return {
    ...params,
    city: resolved.city,
    province: resolved.province,
    country: resolved.country,
  };
}
